import { useMemo } from "react";

import { buildPositionLookup, getOrderSide, getPledgePlusAsset as getAssetFromSymbol, isPositionSLOrder } from "../utils/pledgePlusOrders";

/**
 * Calculate MODE (most frequent value)
 */
function calculateMode(numbers) {
    if (!numbers || numbers.length === 0) return 0;
    const frequency = {};
    let maxFreq = 0, mode = numbers[0];
    numbers.forEach(num => {
        frequency[num] = (frequency[num] || 0) + 1;
        if (frequency[num] > maxFreq) { maxFreq = frequency[num]; mode = num; }
    });
    return mode;
}

/**
 * Mapping from asset name → the corresponding pledgeplus FUT multiplier field name
 * from public.lots_config_pledgeplus_fut
 */
const PP_FUT_MULTIPLIER_MAP = {
    // All MCX commodities share pledgeplus_mcx_multiplier
    GOLD:        'pledgeplus_mcx_multiplier',
    GOLDM:       'pledgeplus_mcx_multiplier',
    SILVER:      'pledgeplus_mcx_multiplier',
    SILVERM:     'pledgeplus_mcx_multiplier',
    CRUDEOIL:    'pledgeplus_mcx_multiplier',
    NATURALGAS:  'pledgeplus_mcx_multiplier',
    COPPER:      'pledgeplus_mcx_multiplier',
    ZINC:        'pledgeplus_mcx_multiplier',
    LEAD:        'pledgeplus_mcx_multiplier',
    ALUMINIUM:   'pledgeplus_mcx_multiplier',
    MENTHAOIL:   'pledgeplus_mcx_multiplier',
    COTTON:      'pledgeplus_mcx_multiplier',
    // Index futures
    NIFTY:       'pledgeplus_nifty_multiplier',
    BANKNIFTY:   'pledgeplus_banknifty_multiplier',
    FINNIFTY:    'pledgeplus_finnifty_multiplier',
    MIDCPNIFTY:  'pledgeplus_midcpnifty_multiplier',
    SENSEX:      'pledgeplus_sensex_multiplier',
};

/**
 * PledgeMini MCX multiplier field (separate column for pledgeplus_mini users)
 */
const PP_MINI_MCX_FIELD = 'pledgemini_mcx_multiplier';

/**
 * Get Multiplier
 * Tries to find specific multiplier (e.g. crudeoil_multiplier), else defaults to 1.
 * Returns ONLY the scalar value for backward-compatible usage inside the hook.
 */
function getMultiplier(userMappings, clientId, asset) {
    if (!userMappings || !clientId || !asset) return 1;
    const mapping = userMappings.find((u) => u.client_id === clientId);
    if (!mapping) return 1;

    const key = `${asset.toLowerCase()}_multiplier`;
    const val = Number(mapping[key]);
    return val > 0 ? val : 1;
}

/**
 * Get both the existing asset-specific multiplier AND the new PledgePlus FUT multipliers.
 * Returns:
 *   mult       — existing commodity/asset multiplier (e.g. crudeoil_multiplier), default 1
 *   futMult    — pledgeplus FUT multiplier from lots_config_pledgeplus_fut, null if not set
 *   miniMult   — pledgemini_mcx_multiplier from lots_config_pledgeplus_fut, null if not set
 */
function getMultipliers(userMappings, clientId, asset) {
    if (!userMappings || !clientId || !asset) return { mult: 1, futMult: null, miniMult: null };
    const mapping = userMappings.find((u) => u.client_id === clientId);
    if (!mapping) return { mult: 1, futMult: null, miniMult: null };

    // Existing asset-specific multiplier (e.g. crudeoil_multiplier)
    const assetKey = `${asset.toLowerCase()}_multiplier`;
    const assetVal = Number(mapping[assetKey]);
    const mult = assetVal > 0 ? assetVal : 1;

    // PledgePlus FUT multiplier from lots_config_pledgeplus_fut
    const futFieldName = PP_FUT_MULTIPLIER_MAP[asset.toUpperCase()];
    const futRaw = futFieldName ? Number(mapping[futFieldName]) : NaN;
    const futMult = (!isNaN(futRaw) && futRaw > 0) ? futRaw : null;

    // PledgeMini MCX multiplier (only relevant for MCX assets)
    const miniRaw = Number(mapping[PP_MINI_MCX_FIELD]);
    const miniMult = (!isNaN(miniRaw) && miniRaw > 0) ? miniRaw : null;

    return { mult, futMult, miniMult };
}

/**
 * Main Hook
 */
export function usePledgePlusOrdersView(orders, userMappings, positions) {
    return useMemo(() => {
        if (!orders || orders.length === 0) {
            return { data: [], baseQtyMap: {}, activeAssets: [] };
        }

        const positionLookup = buildPositionLookup(positions);

        // ------------------------------------------------------
        // 1. Filter & Group Buy Orders by Client + Asset
        // ------------------------------------------------------
        const clientAssetMap = {}; // { clientId: { ASSET: { qty: 0, orders: [] } } }
        const allAssetQtys = {};   // { ASSET: [normalizedQtys...] } - for base qty calc

        orders.forEach(order => {
            // The table includes transit (4) and pending (6) orders.
            const validStatus = String(order.status_no) === "6" || String(order.status_no) === "4";
            if (!validStatus) return;

            // Determine Side
            const side = getOrderSide(order);
            const isBuy = side === "BUY";
            const isSell = side === "SELL";

            if (!isBuy && !isSell) return;

            if (isPositionSLOrder(order, positionLookup)) return;

            // FILTER: Valid Asset
            const asset = getAssetFromSymbol(order.symbol);
            if (!asset) return;

            const clientId = order.clientId || order.client_id;
            const qty = Math.abs(parseFloat(order.qty || 0));

            // Init Client Map
            if (!clientAssetMap[clientId]) clientAssetMap[clientId] = {};
            if (!clientAssetMap[clientId][asset]) {
                clientAssetMap[clientId][asset] = {
                    maxQty: 0,
                    buyQty: 0,
                    sellQty: 0,
                    rawBuyQty: 0,
                    rawSellQty: 0,
                    orders: []
                };
            }

            // FILTERING LOGIC: Checked Covered vs Excess
            // REVERTED: User found "0 Count" confusing (thought orders were missing).
            // Now we show FULL Qty (Gross Orders) regardless of position coverage.
            let displayQty = qty;

            // Aggregate ONLY Display/Excess Qty
            if (displayQty > 0) {
                if (isBuy) clientAssetMap[clientId][asset].buyQty += displayQty;
                if (isSell) clientAssetMap[clientId][asset].sellQty += displayQty;
            }

            // Aggregate RAW Qty
            if (isBuy) clientAssetMap[clientId][asset].rawBuyQty = (clientAssetMap[clientId][asset].rawBuyQty || 0) + qty;
            if (isSell) clientAssetMap[clientId][asset].rawSellQty = (clientAssetMap[clientId][asset].rawSellQty || 0) + qty;

            // Update MaxQty based on RAW quantities (preserves sorting logic)
            clientAssetMap[clientId][asset].maxQty = Math.max(
                clientAssetMap[clientId][asset].rawBuyQty,
                clientAssetMap[clientId][asset].rawSellQty
            );


            clientAssetMap[clientId][asset].orders.push(order);
        });

        // ------------------------------------------------------
        // 2. Base Qty Calculation
        // ------------------------------------------------------
        // We need to normalize by multiplier first.
        // Since we are iterating clients now, let's build the rows and collect normalized qtys simultaneously.
        // Actually, to get true base qty mode, we need all normalized qtys across all clients.

        // First pass: Collect raw quantities per asset/client to normalize later
        // ... handled in step 1.

        // Now iterate clients to build data and collect normalized qtys
        const tempRows = [];
        const activeAssetsSet = new Set();

        Object.keys(clientAssetMap).forEach(clientId => {
            // Find User Mapping
            let userMapping = null;
            if (userMappings) {
                const matches = userMappings.filter((u) => u.client_id === clientId);
                // Prioritize 'dhan_xts' or just take first
                userMapping = matches.find(u => (u.broker || "").toLowerCase().includes("dhan")) || matches[0];
            }

            const row = {
                clientId,
                clientName: userMapping ? userMapping.name : `Client ${clientId}`,
                broker: userMapping ? userMapping.broker : "unknown",
            };

            Object.keys(clientAssetMap[clientId]).forEach(asset => {
                activeAssetsSet.add(asset);
                const assetData = clientAssetMap[clientId][asset];
                // Use getMultipliers to get both existing mult AND new pledgeplus FUT mults
                const { mult, futMult, miniMult } = getMultipliers(userMappings, clientId, asset);

                // Collect for Base Qty Calc
                // Normalize using maxQty (dominant side)
                const normalized = assetData.maxQty / mult;
                if (!allAssetQtys[asset]) allAssetQtys[asset] = [];
                allAssetQtys[asset].push(normalized);

                row[asset] = {
                    multiplier: mult,
                    futMult,    // pledgeplus FUT multiplier (null if not configured)
                    miniMult,   // pledgemini MCX multiplier (null if not configured)
                    buyQty: assetData.buyQty,
                    sellQty: assetData.sellQty,
                    qty: assetData.maxQty, // Keep for sorting/legacy
                    // expectedQty & color will be set in 2nd pass after BaseQty is known
                };
            });

            tempRows.push(row);
        });

        // Calculate Base Qty (Mode)
        const baseQtyMap = {};
        Object.keys(allAssetQtys).forEach(asset => {
            baseQtyMap[asset] = calculateMode(allAssetQtys[asset]);
        });

        // ------------------------------------------------------
        // 3. Final Pass: Validation Colors
        // ------------------------------------------------------
        const activeAssets = Array.from(activeAssetsSet).sort();
        const data = tempRows.map(row => {
            activeAssets.forEach(asset => {
                if (row[asset]) {
                    const cell = row[asset];
                    const base = baseQtyMap[asset] || 0;
                    // If multiplier is 1 (default) and base is huge, it might be correct,
                    // but usually we expect Multiplier * Base = Qty
                    const expected = cell.multiplier * base;

                    cell.expectedQty = expected;

                    // Logic: Match if EITHER BuyQty OR SellQty matches expected
                    // Or if both match?
                    // Usually user has one side. 
                    // If BuyQty > 0 and SellQty == 0 -> Check Buy
                    // If SellQty > 0 and BuyQty == 0 -> Check Sell
                    // If Both -> Check Both? 

                    let isMatch = false;
                    if (cell.buyQty > 0 && cell.sellQty === 0) isMatch = (cell.buyQty === expected);
                    else if (cell.sellQty > 0 && cell.buyQty === 0) isMatch = (cell.sellQty === expected);
                    else if (cell.buyQty > 0 && cell.sellQty > 0) isMatch = (cell.buyQty === expected || cell.sellQty === expected); // Loose match?

                    cell.match = isMatch;
                    cell.color = isMatch ? "green" : "red";
                }
            });
            return row;
        });

        // ------------------------------------------------------
        // 4. Calculate Symbol+Strategy Base Qty (for Modal)
        // ------------------------------------------------------
        // We need { "SYMBOL_STRATEGY": { index: "ASSET", baseQty: MODE, clients: COUNT } }

        const symbolStrategyData = {}; // { KEY: { quantities: [], symbol, strategy, asset } }

        orders.forEach(order => {
            // Re-use logic: Valid Status, Valid Asset
            const validStatus = String(order.status_no) === "6" || String(order.status_no) === "4";
            if (!validStatus) return;
            const asset = getAssetFromSymbol(order.symbol);
            if (!asset) return;

            const side = getOrderSide(order);
            const isBuy = side === "BUY";
            const isSell = side === "SELL";
            if (!isBuy && !isSell) return;

            if (isPositionSLOrder(order, positionLookup)) return;

            const clientId = order.clientId || order.client_id;
            const qty = Math.abs(parseFloat(order.qty || 0));
            const strategy = order.strategy || "NO_STRATEGY";
            const key = `${order.symbol}_${strategy}`;

            if (!symbolStrategyData[key]) {
                symbolStrategyData[key] = {
                    symbol: order.symbol,
                    strategy,
                    asset,
                    clientQuantities: {} // { clientId: maxQty }
                };
            }

            if (!symbolStrategyData[key].clientQuantities[clientId]) {
                symbolStrategyData[key].clientQuantities[clientId] = 0;
            }
            // For symbol strategy base qty, we check if this order adds to the existing logic
            // But wait, are we summing ALL orders for a client? 
            // In the previous logic we just summed everything.
            // Let's stick to summing everything per client per symbol which creates a "Net" or "Total"
            // Actually, for Base Qty calc, we usually want the Position Size.
            // If a client has Buy 50 and Sell 50 (square off), their Position is 0?
            // But these are ORDERS view.
            // Let's just Max(Buy, Sell) for now to estimate intended position size?
            // Or just Sum everything? 
            // Previous code summed `qty` for all buy orders.
            // Let's Sum `qty` but we need to know if we are aggregating buy or sell?
            // Let's aggregate Max(Buy, Sell) per client per symbol.

            // To do that, we need an intermediate step.
            // Let's simplify: Just calculate Mode of ALL orders for now or stick to the "Max of Buy/Sell" pattern efficiently.

            if (!symbolStrategyData[key].clientSideQuantities) symbolStrategyData[key].clientSideQuantities = {};
            if (!symbolStrategyData[key].clientSideQuantities[clientId]) symbolStrategyData[key].clientSideQuantities[clientId] = { buy: 0, sell: 0 };

            if (isBuy) symbolStrategyData[key].clientSideQuantities[clientId].buy += qty;
            if (isSell) symbolStrategyData[key].clientSideQuantities[clientId].sell += qty;
        });

        const symbolStrategyBaseQty = {};
        Object.entries(symbolStrategyData).forEach(([key, group]) => {
            const normalizedQtys = [];

            // Iterate clientSideQuantities to find Max(Buy, Sell) per client
            if (group.clientSideQuantities) {
                Object.entries(group.clientSideQuantities).forEach(([clientId, sides]) => {
                    const maxQ = Math.max(sides.buy, sides.sell);
                    // Use existing scalar getMultiplier — base qty normalization unchanged
                    const mult = getMultiplier(userMappings, clientId, group.asset);
                    normalizedQtys.push(maxQ / mult);
                });
            } else {
                // Fallback if no clientSideQuantities (shouldn't happen with new logic)
            }

            // Calculate Mode of Normalized Quantities
            const mode = calculateMode(normalizedQtys);

            symbolStrategyBaseQty[key] = {
                symbol: group.symbol,
                strategy: group.strategy,
                index: group.asset, // Use 'index' key to match Modal expectations
                baseQty: mode,
                clients: Object.keys(group.clientSideQuantities || {}).length
            };
        });

        return { data, activeAssets, baseQtyMap, symbolStrategyBaseQty };

    }, [orders, userMappings, positions]);
}
